import { useCallback, useEffect, useState } from "react";
import { DownloadButton } from "@/components/tracks/DownloadButton";
import { useNavigate, useParams } from "react-router-dom";
import { MixCover } from "@/components/mix/MixCover";
import { TrackList } from "@/components/tracks/TrackList";
import { Skeleton } from "@/components/ui/Skeleton";
import { mixService } from "@/services/mixService";
import { usePlayer } from "@/hooks/usePlayer";
import { useAuth } from "@/hooks/useAuth";
import { useStickyHeaderTrigger } from "@/hooks/useStickyHeaderTrigger";
import { useToast } from "@/contexts/ToastContext";
import { sfx } from "@/lib/sfx";
import { fmtDurationVerbose } from "@/utils/format";
import type { MixDetail } from "@/types/types";

function shuffled<T>(a: T[]): T[] {
  const b = a.slice();
  for (let i = b.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [b[i], b[j]] = [b[j], b[i]];
  }
  return b;
}

export function MixPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const player = usePlayer();
  const toast = useToast();
  const [mix, setMix] = useState<MixDetail | null>(null);
  const [missing, setMissing] = useState(false);
  const [saving, setSaving] = useState(false);
  const { sentinelRef, visible: stickyVisible } = useStickyHeaderTrigger();

  useEffect(() => {
    setMix(null);
    setMissing(false);
    mixService.get(id).then(setMix).catch(() => setMissing(true));
  }, [id]);

  const playAll = useCallback((shuffle = false) => {
    if (!mix?.tracks.length) return;
    sfx.pop();
    void player.play(shuffle ? shuffled(mix.tracks) : mix.tracks, 0);
  }, [mix, player]);

  const save = useCallback(async () => {
    if (!mix || saving) return;
    setSaving(true);
    try {
      const p = await mixService.save(mix.id);
      sfx.chirp();
      toast.show(`Enregistrée dans ta bibliothèque : ${p.name}`);
      nav(`/playlist/${p.id}`);
    } catch {
      toast.show("Impossible d'enregistrer cette playlist.");
    } finally { setSaving(false); }
  }, [mix, saving, toast, nav]);

  if (missing) {
    return (
      <div style={{ padding: "40px 0" }}>
        <h2>Ce mix n'existe plus</h2>
        <p className="muted">Les playlists conçues pour toi sont renouvelées régulièrement.</p>
        <button className="btn primary" onClick={() => nav("/")}>Retour à l'accueil</button>
      </div>
    );
  }
  if (!mix) {
    return (
      <div className="row" style={{ gap: 28, alignItems: "flex-end" }}>
        <Skeleton width={232} height={232} radius={8} />
        <div className="grow" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Skeleton width={120} /><Skeleton width="60%" height={44} /><Skeleton width="40%" />
        </div>
      </div>
    );
  }

  const who = user?.display_name || user?.username || "toi";
  return (
    <div className="page-hero-wrap">
      <div className="hero-wash" style={{
        background: `linear-gradient(180deg, ${mix.color} 0%, transparent 70%)`, opacity: 0.55,
      }} />

      <div className={"page-sticky-header" + (stickyVisible ? " visible" : "")}>
        <MixCover mix={mix} size={36} />
        <strong className="trunc">{mix.title}</strong>
        <div className="grow" />
        <button className="btn primary" style={{ padding: "8px 14px" }} onClick={() => playAll()}>▶</button>
      </div>

      <header className="hero mix-hero">
        <div className="mix-hero-cover"><MixCover mix={mix} /></div>
        <div className="mix-hero-text">
          <div className="muted" style={{ fontSize: 13 }}>Playlist · Conçue pour {who}</div>
          <h1 className="mix-hero-title">{mix.title}</h1>
          <div className="muted" style={{ fontSize: 14, maxWidth: 640 }}>{mix.description}</div>
          {mix.tags.length > 0 && (
            <div className="mix-tags">{mix.tags.map((t) => <span key={t}>{t}</span>)}</div>
          )}
          <div style={{ fontSize: 13, marginTop: 2 }}>
            <strong>slowson</strong>
            <span className="muted"> · {mix.total} titres, {fmtDurationVerbose(mix.duration_ms)} · {mix.updates}</span>
          </div>
          <div className="row" style={{ marginTop: 10, gap: 8, flexWrap: "wrap" }}>
            <button className="btn primary" onClick={() => playAll()}>▶ Lire</button>
            <button className="btn" onClick={() => playAll(true)}>🔀 Aléatoire</button>
            <button className="btn" onClick={() => void save()} disabled={saving}>
              {saving ? "…" : "＋ Enregistrer"}
            </button>
            <DownloadButton id={`mix:${mix.id}`} label={mix.title} tracks={mix.tracks} />
          </div>
        </div>
      </header>
      <div ref={sentinelRef} style={{ height: 1 }} aria-hidden="true" />

      <TrackList tracks={mix.tracks} />
    </div>
  );
}
